import { Stage } from "./CaseConfiguration.ts";
import { OfferStatus, SettlementOffer, SettlementRange } from "./CaseModel.ts";
import type { AdjustedPairStage } from "./settlementAdjusted.ts";

/** Where in the bargaining range two parties settle, unless the user says otherwise. */
export const MIDPOINT = 0.5;

/**
 * What the user expects of a settlement between two parties (see
 * docs/SettlementProspects.md). The same for both, so stored once per pair.
 */
export interface SettlementChances {
  // the two parties, in sorted order
  parties: [string, string];
  // per stage: the chance of settling at it, if the two get there; what is
  // not entered is 0, which leaves every figure as litigated
  chances?: { [stage in Stage]?: number };
  // where in the range they settle, seen from the first party: 0 is the worst
  // for it, 1 the worst for the other
  position?: number;
}

const pairOf = (partyId: string, opponentId: string): [string, string] =>
  partyId < opponentId ? [partyId, opponentId] : [opponentId, partyId];

export function chancesOf(
  all: SettlementChances[] | undefined,
  partyId: string,
  opponentId: string,
): SettlementChances | undefined {
  const [first, second] = pairOf(partyId, opponentId);
  return all?.find(
    ({ parties }) => parties[0] === first && parties[1] === second,
  );
}

/** Where a pair settles, seen from one of the two. */
export function positionOf(
  chances: SettlementChances | undefined,
  partyId: string,
  opponentId: string,
): number {
  const position = chances?.position ?? MIDPOINT;
  return pairOf(partyId, opponentId)[0] === partyId ? position : 1 - position;
}

const withPair = (
  all: SettlementChances[] | undefined,
  partyId: string,
  opponentId: string,
  change: (pair: SettlementChances) => SettlementChances,
): SettlementChances[] => {
  const existing = chancesOf(all, partyId, opponentId);
  const changed = change(
    existing ?? { parties: pairOf(partyId, opponentId) },
  );
  return existing
    ? (all ?? []).map((pair) => (pair === existing ? changed : pair))
    : [...(all ?? []), changed];
};

export const withChance = (
  all: SettlementChances[] | undefined,
  partyId: string,
  opponentId: string,
  stage: Stage,
  chance: number,
): SettlementChances[] =>
  withPair(all, partyId, opponentId, (pair) => ({
    ...pair,
    chances: { ...pair.chances, [stage]: chance },
  }));

export const withPosition = (
  all: SettlementChances[] | undefined,
  partyId: string,
  opponentId: string,
  position: number,
): SettlementChances[] =>
  withPair(all, partyId, opponentId, (pair) => ({
    ...pair,
    position:
      pairOf(partyId, opponentId)[0] === partyId ? position : 1 - position,
  }));

/** An offer on the table: a draft, or made and not decided. It is at the current stage. */
export const isOpen = (status: OfferStatus): boolean =>
  status === OfferStatus.Draft || status === OfferStatus.Offered;

/** The stage an offer belongs to: an open one has none of its own yet. */
export const stageOfOffer = (
  { status, stage }: Pick<SettlementOffer, "status" | "stage">,
  current: Stage,
): Stage => (isOpen(status) ? current : stage);

/**
 * What a settlement at a position of a stage's range comes to for the party;
 * undefined where the stage has no range the two could agree in.
 */
export function amountAt(
  range: SettlementRange | undefined,
  position: number,
): number | undefined {
  if (!range || range.opponentEv <= range.partyEv) return undefined;
  return range.partyEv + (range.opponentEv - range.partyEv) * position;
}

/** The stages after the judgment take no chance yet: it would depend on the judgment. */
export const settlesBeforeJudgment = (stage: Stage): boolean =>
  stage <= Stage.Trial;

/**
 * What the chances of a pair come to per stage, seen from the first party: a
 * stage is reached only if none before it settled. An offer on the table sets
 * the amount of every stage; without one, a stage without a range takes no
 * chance. The money these settlements make is not worked out here: that takes
 * the whole calculation (see settlementAdjusted.ts).
 */
export function chancesPerStage({
  stages,
  chances,
  position,
  rangeOf,
  onTheTable,
}: {
  stages: Stage[];
  chances: SettlementChances | undefined;
  position: number;
  rangeOf: (stage: Stage) => SettlementRange | undefined;
  onTheTable?: number;
}): AdjustedPairStage[] {
  let after = 1;
  return stages.map((stage) => {
    const chance = chances?.chances?.[stage] ?? 0;
    const range = rangeOf(stage);
    const atPosition = amountAt(range, position);
    const noRange = atPosition === undefined;
    const amount = onTheTable ?? atPosition;
    const counts = onTheTable !== undefined || !noRange;
    const here = counts ? after * chance : 0;
    after -= here;
    return {
      stage,
      chance,
      amount,
      noRange,
      outsideRange:
        onTheTable !== undefined &&
        range !== undefined &&
        !noRange &&
        (onTheTable < range.partyEv || onTheTable > range.opponentEv),
      here,
      after,
    };
  });
}
